import axios from 'axios'
import { calculateTotalSize } from 'services/create-update-site/pro'
import {
  getPresignedUrlCreate,
  getPresignedUrlUpdate,
  processPresignedUrlCreate,
  processPresignedUrlUpdate,
  deleteSiteFiles
} from 'services/site'
import { fetchUserData } from 'Manage/actions'

/** Important: ensure that this is set as identical in backend */
export const MAX_CHUNKS = 10000
export const MIN_CHUNK_SIZE = 10 * 1024 * 1024 // 10MB

window.cancelPresignedUpload = false

export const getSafeFileName = (fileName) => {
  const index = fileName.lastIndexOf('.')
  const name = fileName.substring(0, index)
  const ext = fileName.substring(index + 1).toLowerCase()

  return `${name.replace(/[-\s.]+/g, '-').replace(/['?>~!@%()"{}&#!<>:`+]+/g, '')}.${ext}`
}

export const getFilesMetadata = (allFiles) => {
  const files = allFiles.filter(Boolean)
  if (!files?.length) return []
  return files.map((file) => ({ name: file.name, size: file.size, type: file.type }))
}

const getBatchSize = () => {
  const memory = navigator.deviceMemory || 8
  const cores = navigator.hardwareConcurrency || 4
  const connection = navigator.connection
  const is4G = connection?.effectiveType === '4g'
  const isHighBandwidth = connection?.downlink >= 5

  let batchSize = Math.ceil((memory * cores) / 6)

  if (connection?.saveData) {
    batchSize = Math.min(batchSize, 4)
  } else if (!is4G || !isHighBandwidth) {
    batchSize = Math.ceil(batchSize / 2)
  }

  return Math.max(6, Math.min(batchSize, 32))
}

const checkCancelledPresignedUpload = async ({ domain, site, dispatch, isUpdateSite }) => {
  if (window.cancelPresignedUpload) {
    if (isUpdateSite) {
    } else {
      await deleteSiteFiles({
        domain,
        site
      })
    }
    dispatch(fetchUserData(false))
    return { cancelled: true }
  }
}

const RETRY_DELAY = 2000

const handleGetPresignedUrl = async ({
  file,
  domain,
  newDomain,
  formData,
  config,
  isUpdate,
  uploadProgress,
  totalSize,
  totalUploadedSize,
  isFirstDone = false,
  filesToAddPresignedTotalSize,
  dispatch,
  isUpdateSite,
  isTransferRequestLink,
  uploaderEmail
}) => {
  try {
    newDomain = newDomain || domain
    const chunkProgress = {}

    const CHUNK_SIZE = Math.max(MIN_CHUNK_SIZE, Math.ceil(file?.size / MAX_CHUNKS))
    const filesToAddPresigned = []
    /** For S3 CompleteMultipartUploadCommand */
    const multipartInfo = []

    const safeFileName = getSafeFileName(file.name)
    const formDataGetPresignedUrl = new FormData()

    formDataGetPresignedUrl.delete('filesToAdd')
    formDataGetPresignedUrl.append('key', `${domain}/${safeFileName}`)
    formDataGetPresignedUrl.append('domain', domain)
    formDataGetPresignedUrl.append('newDomain', newDomain)

    const fileInfo = {
      name: safeFileName,
      originalFilename: safeFileName,
      s3FilePath: `${domain}/${safeFileName}`,
      type: file.type,
      size: file.size,
      filesToAddPresignedSize: totalSize,
      filesToAddPresignedTotalSize
    }
    filesToAddPresigned.push(fileInfo)
    formDataGetPresignedUrl.append('filesToAddPresigned', JSON.stringify([fileInfo]))
    formDataGetPresignedUrl.append('isTransferRequestLink', JSON.stringify(isTransferRequestLink))
    formDataGetPresignedUrl.append('uploaderEmail', uploaderEmail)

    const presignedUrlResponse = isUpdate
      ? await getPresignedUrlUpdate({ ...config, domain }, formDataGetPresignedUrl, isTransferRequestLink)
      : await getPresignedUrlCreate(config, formDataGetPresignedUrl)

    const { presignedUrls, uploadId, key, link } = presignedUrlResponse.data

    /* Chunks for multipart upload */
    const chunks = Math.ceil(file.size / CHUNK_SIZE)
    const uploadFunctions = []

    /** For storing ETag and PartNumber of completed multipart chunks */
    const completedParts = []

    /** Check for cancelled presigned upload */
    if (window.cancelPresignedUpload) {
      return await checkCancelledPresignedUpload({ domain, site: link, dispatch, isUpdateSite })
    }

    for (let i = 0; i < chunks; i++) {
      const start = i * CHUNK_SIZE
      const end = Math.min(start + CHUNK_SIZE, file.size)
      const chunk = file.slice(start, end)
      const partNumber = i + 1

      /** Upload multipart chunks to corresponding presigned urls */
      const uploadChunk = async () => {
        chunkProgress[partNumber] = 0

        /** Loop to keep retrying chunks if offline */
        while (true) {
          try {
            if (!navigator.onLine) {
              /** Loop to keep checking if offline every 2 seconds and continue if online */
              while (!navigator.onLine) {
                await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY))
              }
              continue
            }

            const response = await axios.put(presignedUrls[i], chunk, {
              headers: {
                'Content-Type': file.type
              },
              transformRequest: [(data) => data],
              withCredentials: false,
              onUploadProgress: (progressEvent) => {
                const previousProgress = chunkProgress[partNumber] || 0
                const currentProgress = (progressEvent.loaded / progressEvent.total) * chunk.size
                const diff = currentProgress - previousProgress

                totalUploadedSize.uploadedSize += diff
                chunkProgress[partNumber] = currentProgress

                if (uploadProgress) {
                  const percentComplete = Math.min(Math.round((totalUploadedSize.uploadedSize / totalSize) * 100), 99)
                  uploadProgress({ loaded: percentComplete, total: 100 }, { size: totalSize }, true, isFirstDone)
                }
              }
            })

            completedParts.push({
              ETag: response.headers.etag,
              PartNumber: partNumber
            })
            break
          } catch (error) {
            /** If error, wait for 2 seconds and continue, the chunk will be retried */
            await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY))
            continue
          }
        }
      }

      uploadFunctions.push(uploadChunk)
    }

    /** Process chunks in batches */
    const BATCH_SIZE = getBatchSize()
    for (let i = 0; i < uploadFunctions.length; i += BATCH_SIZE) {
      /** Check for cancelled presigned upload  */
      if (window.cancelPresignedUpload) {
        return await checkCancelledPresignedUpload({ domain, site: link, dispatch, isUpdateSite })
      }

      const batch = uploadFunctions.slice(i, i + BATCH_SIZE)
      await Promise.all(batch.map((fn) => fn()))
    }

    multipartInfo.push({
      key,
      uploadId,
      parts: completedParts.sort((a, b) => a.PartNumber - b.PartNumber)
    })

    return { filesToAddPresigned, multipartInfo }
  } catch (error) {
    console.error(error)
    throw error
  }
}

const handleProcessPresignedUrl = async ({
  formData,
  domain,
  newDomain,
  filesToAddPresigned,
  multipartInfo,
  config,
  isUpdate,
  isPresignedUploadLastFile = false,
  filesToAddPresignedMetadata,
  isPresignedUploadCancelled = false,
  isTransferRequestLink,
  uploaderEmail,
  processFunction
}) => {
  newDomain = newDomain || domain

  formData.delete('filesToAdd')
  formData.delete('filesToAddPresigned')
  formData.delete('multipartInfo')
  formData.delete('newDomain')
  formData.delete('isPresignedUploadLastFile')
  formData.delete('filesToAddPresignedMetadata')
  formData.delete('isPresignedUploadCancelled')

  formData.append('newDomain', newDomain)
  if (filesToAddPresigned) formData.append('filesToAddPresigned', JSON.stringify(filesToAddPresigned))
  if (multipartInfo) formData.append('multipartInfo', JSON.stringify(multipartInfo))
  formData.append('isPresignedUploadLastFile', isPresignedUploadLastFile)
  formData.append('filesToAddPresignedMetadata', JSON.stringify(filesToAddPresignedMetadata))
  formData.append('isPresignedUploadCancelled', isPresignedUploadCancelled)
  formData.append('isTransferRequestLink', JSON.stringify(isTransferRequestLink))
  formData.append('uploaderEmail', uploaderEmail)

  if (processFunction) {
    return await processFunction(formData)
  }

  return isUpdate
    ? await processPresignedUrlUpdate({ ...config, domain }, formData, isTransferRequestLink)
    : await processPresignedUrlCreate(config, formData)
}

export const cancelPresignedUpload = async ({ domain, formData, config }) => {
  try {
    return await handleProcessPresignedUrl({
      formData,
      domain,
      newDomain: domain,
      filesToAddPresigned: null,
      multipartInfo: null,
      config,
      isUpdate: true,
      isPresignedUploadLastFile: true,
      filesToAddPresignedMetadata: { completedFiles: [] },
      isPresignedUploadCancelled: true
    })
  } catch (error) {
    console.error(error)
  }
}

export const handlePresignedUploads = async ({
  files,
  domain,
  newDomain,
  formData,
  config,
  isUpdate,
  uploadProgress,
  isLastFile,
  filesToAddPresignedTotalSize,
  filesToAddPresignedMetadata,
  dispatch,
  isUpdateSite,
  isTransferRequestLink = false,
  uploaderEmail,
  processFunction
}) => {
  localStorage.removeItem('cancelPresignedUpload')

  if (!files?.length) {
    if (isUpdate) {
      return await handleProcessPresignedUrl({
        formData,
        domain,
        newDomain,
        filesToAddPresigned: null,
        multipartInfo: null,
        config,
        isUpdate: true,
        isPresignedUploadLastFile: true,
        filesToAddPresignedMetadata,
        isPresignedUploadCancelled: false,
        isTransferRequestLink,
        uploaderEmail,
        processFunction
      })
    }
    return null
  }

  const totalSize = await calculateTotalSize(files)
  const uploadedSizeRef = { uploadedSize: 0 }

  let handleGetPresignedUrlresult
  let handleProcessPresignedUrlresult

  for (let i = 0; i < files.length; i++) {
    const isPresignedUploadLastFile = isLastFile !== undefined ? isLastFile : i === files.length - 1
    const file = files[i]
    const filesMetadata = {
      ...filesToAddPresignedMetadata,
      completedFiles: [...filesToAddPresignedMetadata.completedFiles, ...getFilesMetadata(files.slice(0, i + 1))]
    }

    handleGetPresignedUrlresult = await handleGetPresignedUrl({
      file,
      domain,
      newDomain,
      formData,
      config,
      isUpdate: file === files[0] ? isUpdate : true,
      uploadProgress,
      totalSize,
      totalUploadedSize: uploadedSizeRef,
      isFirstDone: file !== files[0] ? true : false,
      filesToAddPresignedTotalSize,
      dispatch,
      isUpdateSite,
      isTransferRequestLink,
      uploaderEmail
    })

    /** Do not process for cancelled presigned uploads for create, for update process with flag isPresignedUploadCancelled */
    if (handleGetPresignedUrlresult?.cancelled && !isUpdateSite) {
      window.cancelPresignedUpload = false
      throw new Error('UPLOAD_CANCELLED')
    }

    handleProcessPresignedUrlresult = await handleProcessPresignedUrl({
      formData,
      domain,
      newDomain,
      filesToAddPresigned: handleGetPresignedUrlresult.filesToAddPresigned,
      multipartInfo: handleGetPresignedUrlresult.multipartInfo,
      config,
      isUpdate: true,
      isPresignedUploadLastFile: isPresignedUploadLastFile,
      filesToAddPresignedMetadata: filesMetadata,
      isPresignedUploadCancelled: handleGetPresignedUrlresult?.cancelled && isUpdateSite,
      isTransferRequestLink,
      uploaderEmail,
      processFunction
    })
  }

  window.cancelPresignedUpload = false
  return handleProcessPresignedUrlresult
}
